import { createForm } from '@formily/core';
import type { Field, IFormProps } from '@formily/core';
import { useField, useFieldSchema } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { Button, Modal } from 'antd';
import type { ButtonProps } from 'antd/es/button';
import styled from 'styled-components';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';

import type { IFormilySchema, IComponentSchema } from '../../types';
import { useToggleVisibility } from '../../../hooks';
import { ConfirmModal } from '../../../components';
import { usePage } from '../../context/PageContext';
import {
  applyChildCollectionValues,
  buildInitialValuesFromSchemaV2,
} from '../form-array-table/utils';
import { findRootFormValues } from '../../utils';

import AddItemForm from './AddItemForm';
import RecursionNode from './RecursionNode';
import { buildTree, getDescendantIds, parseSubSchema } from './utils';
import type { IChildCollection, ITreeNode } from './utils';

export interface IFormRecursionItemProps {
  title?: string;
  childSchema?: IFormilySchema;
  modalTitle?: string;
  modelWidth?: string;
  mode?: 'single' | 'multiple';
  canEditItem?: boolean;
  canDeleteItem?: boolean;
  canAddChildItem?: boolean;
  addItemButtonText?: string;
  addNestedItemButtonText?: string;
  editItemButtonText?: string;
  deleteItemButtonText?: string;
  buttonText?: string;
  buttonType?: ButtonProps['type'];
  buttonSize?: ButtonProps['size'];
  buttonBlock?: boolean;
  buttonDanger?: boolean;
  buttonDisabled?: boolean;
  buttonGhost?: boolean;
  buttonVisible?: boolean;
  cancelButtonText?: string;
  saveButtonText?: string;
  customComponentSchema?: IComponentSchema<any> | null;
  customComponentStyle?: any;
  childCollections?: IChildCollection[];
}

type FormRecursionRecord = Record<string, unknown>;

interface ICollectionTarget {
  collection: IChildCollection;
  ownerId: string;
  rowId?: string | null;
}

// Default schema is used just for rendering the component in builder
const FormRecursionItem = observer(
  ({ schema: defaultSchema }: { schema: IFormilySchema }) => {
    const field = useField<Field>();
    const componentProps = (field?.componentProps ||
      defaultSchema?.['x-component-props'] ||
      {}) as IFormRecursionItemProps;
    const pageData = usePage();

    const schema = useFieldSchema() || defaultSchema;

    const {
      title,
      childSchema,
      modalTitle,
      modelWidth,
      mode = 'single',
      canEditItem = true,
      canDeleteItem = true,
      canAddChildItem = true,
      buttonText,
      buttonType = 'primary',
      buttonSize = 'middle',
      buttonBlock = false,
      buttonDanger = false,
      buttonDisabled = false,
      buttonGhost = false,
      buttonVisible = true,
      cancelButtonText,
      saveButtonText,
      customComponentSchema: rawCustomComponentSchema,
      customComponentStyle,
      addItemButtonText,
      addNestedItemButtonText,
      editItemButtonText,
      deleteItemButtonText,
      childCollections: rawChildCollections,
    } = componentProps;

    const componentConfig = schema[
      'x-component-props-config'
    ] as IFormRecursionItemProps;

    const {
      canAddChildItem: canAddChildItemConfig,
      canDeleteItem: canDeleteItemConfig,
      canEditItem: canEditItemConfig,
      customComponentStyle: customComponentStyleConfig,
    } = componentConfig || {};

    const { disabled } = field || {};

    const { isVisible, show, hide } = useToggleVisibility();
    const {
      isVisible: isDeleteConfirmVisible,
      show: showDeleteConfirm,
      hide: hideDeleteConfirm,
    } = useToggleVisibility();

    const [editingId, setEditingId] = useState<string | null>(null);
    const [parentIdForNew, setParentIdForNew] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [actionTitle, setActionTitle] = useState<string>();
    const [collectionTarget, setCollectionTarget] =
      useState<ICollectionTarget | null>(null);
    const [collectionDeleteTarget, setCollectionDeleteTarget] =
      useState<ICollectionTarget | null>(null);

    // Parsing child schema because of Formily problem with variables
    const withChildSchemaForm: IFormilySchema | undefined = useMemo(() => {
      if (
        !childSchema ||
        !childSchema?.properties?.childSchemaForm ||
        typeof childSchema?.properties?.childSchemaForm !== 'string'
      )
        return childSchema;
      return {
        ...childSchema,
        properties: {
          ...childSchema?.properties,
          childSchemaForm: JSON.parse(childSchema?.properties?.childSchemaForm),
        },
      };
    }, [childSchema]);

    // Parsing custom component schema because of Formily problem with variables
    const customComponentSchema = useMemo(() => {
      if (!rawCustomComponentSchema) return null;
      if (typeof rawCustomComponentSchema === 'string') {
        return JSON.parse(rawCustomComponentSchema);
      }
      return rawCustomComponentSchema;
    }, [rawCustomComponentSchema]);

    const childCollections = useMemo<IChildCollection[]>(
      () =>
        (Array.isArray(rawChildCollections) ? rawChildCollections : []).map(
          collection => ({
            ...collection,
            formSchema: parseSubSchema(collection.formSchema),
            cardSchema: parseSubSchema(collection.cardSchema),
          })
        ),
      [rawChildCollections]
    );

    useEffect(() => {
      if (
        field?.initialValue &&
        Array.isArray(field.initialValue) &&
        field.initialValue.length > 0
      ) {
        const rootData = findRootFormValues(field.form);
        const transformedValues = buildInitialValuesFromSchemaV2(
          field.initialValue,
          withChildSchemaForm,
          rootData
        );
        const withCollections = applyChildCollectionValues(
          transformedValues,
          field.initialValue,
          childCollections,
          rootData
        );
        if (!isEqual(withCollections, field.initialValue)) {
          field.setInitialValue(withCollections);
        }
      }
    }, [field?.initialValue, childCollections]);

    const dataSource = useMemo(
      () =>
        Array.isArray(field?.value)
          ? field.value.filter((item: FormRecursionRecord) => !item.toBeDeleted)
          : [],
      [field?.value]
    );

    const treeData = useMemo(
      () => buildTree(dataSource, childCollections),
      [dataSource, childCollections]
    );

    const collectionRow = useMemo(() => {
      if (!collectionTarget?.rowId) return undefined;
      const owner = dataSource.find(
        item => item.id === collectionTarget.ownerId
      );
      const rows = owner?.[collectionTarget.collection.field];
      return Array.isArray(rows)
        ? rows.find(
            (row: FormRecursionRecord) => row.id === collectionTarget.rowId
          )
        : undefined;
    }, [collectionTarget, dataSource]);

    // Collection row forms expect `$parent` to be the package that owns the row
    // (as when they lived in the package's own form), so give them that form.
    const collectionParentForm = useMemo(() => {
      if (!collectionTarget) return undefined;
      const owner = dataSource.find(
        item => item.id === collectionTarget.ownerId
      );
      // A collection the package has no rows in yet must still read as `[]`,
      // as its table field did, or expressions over it get `null`.
      const values = childCollections.reduce(
        (acc, collection) =>
          Array.isArray(acc[collection.field])
            ? acc
            : { ...acc, [collection.field]: [] },
        { ...owner }
      );
      return createForm({
        values,
        parent: field?.form,
      } as IFormProps<any>);
    }, [collectionTarget, dataSource, childCollections, field?.form]);

    const updateCollectionRows = useCallback(
      (
        target: ICollectionTarget,
        updater: (rows: FormRecursionRecord[]) => FormRecursionRecord[]
      ) => {
        field.setValue(
          (field.value || []).map((item: FormRecursionRecord) => {
            if (item.id !== target.ownerId) return item;
            const rows = item[target.collection.field];
            return {
              ...item,
              [target.collection.field]: updater(
                Array.isArray(rows) ? rows : []
              ),
            };
          })
        );
      },
      [field]
    );

    const editingItem = useMemo(
      () =>
        editingId != null
          ? dataSource.find(item => item.id === editingId)
          : undefined,
      [editingId, dataSource]
    );

    const handleAddRoot = useCallback(() => {
      setParentIdForNew(null);
      setEditingId(null);
      setActionTitle(buttonText);
      show();
    }, [show, buttonText]);

    const handleAddChild = useCallback(
      (item: FormRecursionRecord, title: string) => {
        setParentIdForNew(item.id as string);
        setEditingId(null);
        setActionTitle(title);
        show();
      },
      [show]
    );

    const handleEdit = useCallback(
      (item: FormRecursionRecord, title: string) => {
        setEditingId(item.id as string);
        setParentIdForNew(null);
        setActionTitle(title);
        show();
      },
      [show]
    );

    const handleDelete = useCallback(
      (item: FormRecursionRecord) => {
        setDeletingId(item.id as string);
        showDeleteConfirm();
      },
      [showDeleteConfirm]
    );

    const handleAddCollectionRow = useCallback(
      (item: FormRecursionRecord, collection: IChildCollection) => {
        setEditingId(null);
        setParentIdForNew(null);
        setCollectionTarget({
          collection,
          ownerId: item.id as string,
          rowId: null,
        });
        show();
      },
      [show]
    );

    const handleEditCollectionRow = useCallback(
      (node: ITreeNode) => {
        if (!node.collection || !node.ownerId) return;
        setEditingId(null);
        setParentIdForNew(null);
        setCollectionTarget({
          collection: node.collection,
          ownerId: node.ownerId,
          rowId: node.item.id as string,
        });
        show();
      },
      [show]
    );

    const handleDeleteCollectionRow = useCallback(
      (node: ITreeNode) => {
        if (!node.collection || !node.ownerId) return;
        setCollectionDeleteTarget({
          collection: node.collection,
          ownerId: node.ownerId,
          rowId: node.item.id as string,
        });
        showDeleteConfirm();
      },
      [showDeleteConfirm]
    );

    const handleCancel = useCallback(() => {
      setEditingId(null);
      setParentIdForNew(null);
      setCollectionTarget(null);
      hide();
    }, [hide]);

    const handleSubmit = useCallback(
      (values: FormRecursionRecord) => {
        if (collectionTarget) {
          const { rowId } = collectionTarget;
          updateCollectionRows(collectionTarget, rows =>
            rowId
              ? rows.map(row =>
                  row.id === rowId ? { ...row, ...values } : row
                )
              : [...rows, { ...values, id: uuidv4(), _formLocal: true }]
          );
          handleCancel();
          return;
        }
        if (editingId !== null) {
          field.setValue(
            (field.value || []).map((item: FormRecursionRecord) =>
              item.id === editingId ? { ...item, ...values } : item
            )
          );
        } else {
          field.setValue([
            ...(field.value || []),
            {
              ...values,
              id: uuidv4(),
              parent: {
                id: parentIdForNew,
              },
              _formLocal: true,
            },
          ]);
        }
        handleCancel();
      },
      [
        editingId,
        parentIdForNew,
        field,
        handleCancel,
        collectionTarget,
        updateCollectionRows,
      ]
    );

    const handleConfirmDelete = useCallback(() => {
      if (collectionDeleteTarget) {
        const { rowId } = collectionDeleteTarget;
        updateCollectionRows(collectionDeleteTarget, rows => {
          const target = rows.find(row => row.id === rowId);
          return target?._formLocal
            ? rows.filter(row => row.id !== rowId)
            : rows.map(row =>
                row.id === rowId ? { ...row, toBeDeleted: true } : row
              );
        });
        setCollectionDeleteTarget(null);
        hideDeleteConfirm();
        return;
      }
      if (deletingId) {
        const target = (field.value || []).find(
          (i: FormRecursionRecord) => i.id === deletingId
        );
        if (target?._formLocal) {
          const idsToRemove = getDescendantIds(field.value || [], deletingId);
          field.setValue(
            (field.value || []).filter(
              (i: FormRecursionRecord) => !idsToRemove.has(i.id as string)
            )
          );
        } else {
          const idsToRemove = getDescendantIds(field.value || [], deletingId);
          field.setValue(
            (field.value || []).map((i: FormRecursionRecord) =>
              idsToRemove.has(i.id as string) ? { ...i, toBeDeleted: true } : i
            )
          );
        }
      }
      setDeletingId(null);
      hideDeleteConfirm();
    }, [
      deletingId,
      field,
      hideDeleteConfirm,
      collectionDeleteTarget,
      updateCollectionRows,
    ]);

    const handleCancelDelete = useCallback(() => {
      setDeletingId(null);
      setCollectionDeleteTarget(null);
      hideDeleteConfirm();
    }, [hideDeleteConfirm]);

    const activeFormSchema = collectionTarget
      ? collectionTarget.collection.formSchema
      : withChildSchemaForm;

    const activeModalTitle = collectionTarget
      ? collectionTarget.collection.modalTitle ||
        collectionTarget.collection.label ||
        ''
      : modalTitle || actionTitle || buttonText || '';

    return (
      <div>
        {(title || buttonText) && (
          <Header>
            <h3 style={{ margin: 0 }}>{title || ''}</h3>
            {buttonVisible &&
              !disabled &&
              !(mode === 'single' && treeData.length > 0) && (
                <Button
                  type={buttonType}
                  size={buttonSize}
                  block={buttonBlock}
                  danger={buttonDanger}
                  disabled={buttonDisabled}
                  ghost={buttonGhost}
                  onClick={handleAddRoot}
                >
                  {buttonText}
                </Button>
              )}
          </Header>
        )}
        {treeData.map(node => (
          <RecursionNode
            style={{
              value: customComponentStyle,
              config: customComponentStyleConfig,
            }}
            key={node.item.id as string}
            node={node}
            depth={0}
            customComponentSchema={customComponentSchema}
            context={pageData?.context}
            canAddChild={{
              value: canAddChildItem,
              config: canAddChildItemConfig,
            }}
            canEdit={{ value: canEditItem, config: canEditItemConfig }}
            canDelete={{ value: canDeleteItem, config: canDeleteItemConfig }}
            childCollections={childCollections}
            disabled={disabled}
            onAddChild={handleAddChild}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAddCollectionRow={handleAddCollectionRow}
            onEditCollectionRow={handleEditCollectionRow}
            onDeleteCollectionRow={handleDeleteCollectionRow}
            addItemButtonText={addItemButtonText}
            addNestedItemButtonText={addNestedItemButtonText}
            editItemButtonText={editItemButtonText}
            deleteItemButtonText={deleteItemButtonText}
          />
        ))}
        {activeFormSchema && (
          <Modal
            open={isVisible}
            onCancel={handleCancel}
            footer={null}
            title={activeModalTitle}
            destroyOnHidden
            maskClosable={false}
            width={modelWidth || 720}
          >
            <AddItemForm
              schema={activeFormSchema}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              initialValues={
                collectionTarget
                  ? collectionRow || {}
                  : editingItem || {
                      parent: {
                        id:
                          parentIdForNew || editingItem?.parentId || undefined,
                      },
                    }
              }
              parentForm={collectionParentForm || field?.form}
              context={pageData.context}
              cancelButtonText={cancelButtonText}
              saveButtonText={saveButtonText}
            />
          </Modal>
        )}
        <ConfirmModal
          isVisible={isDeleteConfirmVisible}
          title="Delete Item"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        >
          <p>
            {collectionDeleteTarget
              ? 'Are you sure you want to delete this item?'
              : 'Are you sure you want to delete this item and all its children?'}
          </p>
        </ConfirmModal>
      </div>
    );
  }
);

export default FormRecursionItem;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
